"use client";

import { useRouter } from "@/i18n/navigation";
import { useState } from "react";
import * as z from "zod";

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/shadcn/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import PlanCard from "@/components/plan-card";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import useStore from "@/store/useStore";
import axios from "axios";
import { cn } from "cn";
import { useTranslations } from "next-intl";
import { Textarea } from "./shadcn/textarea";

async function validateUserName(nickName: string) {
    const data = { gifted_username: nickName };
    const { data: userCheckResponse } = await axios.post(
        "/api/user/check-exist",
        data,
        {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        },
    );

    if (userCheckResponse !== null) {
        return true;
    } else {
        return false;
    }
}

// Messages come from the `checkout` catalog, so the schema is built per render.
const buildFormSchema = (t: (key: string) => string) =>
    z.object({
        gifted_username: z
            .string()
            .min(1, { message: "Bu alan gereklidir." })
            .pipe(
                z.string().refine((val) => validateUserName(val), {
                    message: t("userNotInDb"),
                }),
            ),
        gift_message: z.string().optional(),

        gifted_user_id: z.string().min(1, { message: "Bu alan gereklidir." }),
        gifted_usermail: z.string().min(1, { message: "Bu alan gereklidir." }),
    });

type ValidationSchema = z.infer<ReturnType<typeof buildFormSchema>>;

export default function HediyeForm({ products }: { products: any }) {
    const t = useTranslations("checkout");
    const formSchema = buildFormSchema(t);
    const router = useRouter();
    const { product, setProduct } = useStore((state) => state);
    const [selectedPlan, setSelectedPlan] = useState<any>(null);
    const [giftUser, setGiftUser] = useState("");
    const [giftUserId, setGiftUserId] = useState("");
    const [giftUserMail, setGiftUserMail] = useState("");

    const form = useForm<ValidationSchema>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            gifted_username: "",
            gift_message: "",
            gifted_user_id: "",
            gifted_usermail: "",
        },
    });
    const {
        control,
        formState: { errors },
    } = form;

    const handleOnChange = async (value: string) => {
        if (value.length > 3) {
            // setGiftUserId("");
            // setGiftUserMail("");
            const data = { gifted_username: value };
            // axios call to /api/
            const { data: userCheckResponse } = await axios.post(
                "/api/user/check-exist",
                data,
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                },
            );

            if (userCheckResponse !== null) {
                form.setValue("gifted_user_id", `${userCheckResponse.id}`);
                setGiftUserId(`${userCheckResponse.id}`);

                form.setValue("gifted_usermail", `${userCheckResponse.email}`);
                setGiftUserMail(userCheckResponse.email);
            } else {
                setGiftUserId("");
                form.setValue("gifted_user_id", "");
                setGiftUserMail("");
                form.setValue("gifted_usermail", "");
            }
            setGiftUser(value);
        } else {
            setGiftUser(value);
        }
    };
    const onSubmit = (values: ValidationSchema) => {
        setProduct({
            ...product,
            Id: selectedPlan ? selectedPlan.Id : products[0].Id,
            Title: selectedPlan ? selectedPlan.Title : products[0].Title,
            Price: selectedPlan ? selectedPlan.Price : products[0].Price,
            Type: "giftOrder",
            Duration: selectedPlan
                ? selectedPlan.Duration
                : products[0].Duration,
            Amount: 1,
            gifted_user_id: giftUserId,
            gifted_usermail: giftUserMail,
            gifted_username: giftUser,
            gift_message: values.gift_message || "",
        });
        setProduct({ ...product, ...values });
        router.push("/premium/checkout");
    };
    const handleOnClick = (plan: any) => {
        setSelectedPlan({
            Id: plan.Id,
            Title: plan.Title,
            Price: plan.Price,
            Duration: plan.Duration,
        });
        setProduct({
            ...product,
            Id: plan.Id,
            Title: plan.Title,
            Price: plan.Price,
            Type: "giftOrder",
            Duration: plan.Duration,
            Amount: 1,
        });
    };
    const price = selectedPlan?.Price ? selectedPlan?.Price : products[0].Price;

    const totalPrice = price * 1;

    return (
        <Form {...form}>
            <form
                className="flex flex-col"
                onSubmit={() => form.handleSubmit(onSubmit)}
            >
                <div className="grid grid-cols-1 gap-8">
                    <div className="flex flex-col gap-4">
                        <FormField
                            control={control}
                            name="gifted_username"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center justify-between">
                                        {t("recipientUsername")}
                                        <FormMessage>
                                            {errors.gifted_username?.message}
                                        </FormMessage>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            className={cn(
                                                "placeholder:font-medium",
                                                {
                                                    "border-destructive":
                                                        errors.gifted_username
                                                            ?.message,
                                                },
                                            )}
                                            {...field}
                                            value={giftUser}
                                            onChangeCapture={(e) =>
                                                handleOnChange(
                                                    e.currentTarget.value,
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="gifted_user_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="hidden">
                                        {t("recipientUserId")}
                                        <FormMessage>
                                            {errors.gifted_user_id?.message}
                                        </FormMessage>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="hidden"
                                            className={cn(
                                                "placeholder:font-medium",
                                                {
                                                    "border-destructive":
                                                        errors.gifted_user_id
                                                            ?.message,
                                                },
                                            )}
                                            {...field}
                                            value={giftUserId}
                                            onChangeCapture={(e) =>
                                                setGiftUserId(
                                                    e.currentTarget.value,
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="gifted_usermail"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="hidden">
                                        mail
                                        <FormMessage>
                                            {errors.gifted_usermail?.message}
                                        </FormMessage>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="hidden"
                                            className={cn(
                                                "placeholder:font-medium",
                                                {
                                                    "border-destructive":
                                                        errors.gifted_usermail
                                                            ?.message,
                                                },
                                            )}
                                            {...field}
                                            value={giftUserMail}
                                            onChangeCapture={(e) =>
                                                setGiftUserMail(
                                                    e.currentTarget.value,
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="gift_message"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center justify-between">
                                        {t("recipientMessage")}
                                        <FormMessage>
                                            {errors.gift_message?.message}
                                        </FormMessage>
                                    </FormLabel>
                                    <FormControl>
                                        <Textarea
                                            className={cn(
                                                "bg-background placeholder:font-medium",
                                                {
                                                    "border-destructive":
                                                        errors.gift_message
                                                            ?.message,
                                                },
                                            )}
                                            {...field}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {products.map((plan: any) => (
                        <PlanCard
                            key={`Products-${plan.Id}`}
                            onClick={() => handleOnClick(plan)}
                            item={plan}
                        />
                    ))}
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-baseline gap-1">
                        <span>Toplam Fiyat:</span>
                        <span className="text-2xl font-bold">
                            {totalPrice} TL
                        </span>
                    </div>
                    <Button
                        onClick={form.handleSubmit(onSubmit)}
                        className="w-40 justify-center"
                    >
                        Hediye Edin!
                    </Button>
                </div>
            </form>
        </Form>
    );
}
